import {ChangeDetectorRef, Component, Input, NgZone, OnInit, ViewChild} from '@angular/core';
import {ContactRepository} from '../../states/contact.repository';
import {ContactModel} from '../../models/contact.model';
import {debounceTime, distinctUntilChanged, Subject, take} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {Scroller} from 'primeng/scroller';
import {ManagementHelperService} from '../../Service/management-helper.service';
import {ActivatedRoute, Router} from '@angular/router';
import {AnimalWhereaboutsRepository} from 'src/app/modules/animals/states/animal-whereabouts.repository';
import {AnimalOriginRepository} from 'src/app/modules/animals/states/animal-origin.repository';
import {WaitingDialogService} from 'src/app/shared/services/waiting-dialog.service';

@Component({
  selector: 'app-contact-management',
  templateUrl: './contact-management.component.html',
  styleUrls: ['./contact-management.component.scss'],
  standalone: false
})
export class ContactManagementComponent implements OnInit {
  @ViewChild(Scroller) scroller!: Scroller;
  /**
   * `true`: Tab „Tierärzte“ — nur Kontakte mit `isVeterinary`, neue Kontakte sind vorbelegt.
   * `false`: Tab „Kontakte“ — Tierärzte sind ausgeblendet (gilt nur für die Kontaktverwaltung).
   */
  @Input() onlyVeterinarians: boolean = false;
  alive: boolean = true;
  groupedContacts: Record<string, ContactModel[]> = {};
  alphabet: string[] = [];

  selectedLetter: string = '';
  filteredContacts: ContactModel[] = [];
  visibleContacts: ContactModel[] = [];

  searchText: string = '';
  private readonly searchSubject = new Subject<string>();
  searchedContacts: ContactModel[] = [];

  openCreateContactModal: boolean = false;
  openEditContactModal: boolean = false;

  newContact: ContactModel = {} as ContactModel;
  validationStatusChanged: any;

  highlightedContactId: number | null = null;

  mergeMode = false;
  openMergeModal: boolean = false;
  mergedContact: Partial<ContactModel> = {};
  conflictingFields: Partial<Record<keyof ContactModel, string[]>> = {};
  selectedMergeContacts: ContactModel[] = [];

  isLoading: boolean = true;

  constructor(
    private readonly _contactRepository: ContactRepository,
    private readonly _mgmtHelper: ManagementHelperService,
    private readonly cdr: ChangeDetectorRef,
    private readonly zone: NgZone,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly _whereAboutRepository: AnimalWhereaboutsRepository,
    private readonly _originRepository: AnimalOriginRepository,
    private readonly _waitingDialogService: WaitingDialogService
  ) {
  }

  ngOnInit(): void {
    this.alphabet = this._mgmtHelper.getAlphabetWithSpecial();
    this._contactRepository.loadAllIfNeeded();

    this._contactRepository.allLoaded$
      .pipe(distinctUntilChanged())
      .subscribe(loaded => {
        this.isLoading = !loaded;
        this.cdr.detectChanges();
      });

    this._contactRepository.getAllWhenLoaded$().subscribe(allContacts => {
      const contacts = allContacts.filter(contact => this.belongsToTab(contact));
      const grouped = this._mgmtHelper.groupByInitial(contacts, 'lastName');
      for (const key in grouped) {
        grouped[key] = grouped[key].sort((a, b) =>
          (a.lastName?.toLowerCase() || '').localeCompare(b.lastName?.toLowerCase() || '')
        );
      }
      this.groupedContacts = grouped;

      const qp = this.route.snapshot.queryParamMap;
      const qpLetter = (qp.get('letter') || '').toUpperCase();
      const initialLetter =
        (qpLetter && this.alphabet.includes(qpLetter) && grouped[qpLetter]?.length ? qpLetter : null)
        || this.selectedLetter
        || this.alphabet.find(l => grouped[l]?.length)
        || this.alphabet[0];

      this.filterByLetter(initialLetter);

      const anchorId = Number(qp.get('anchorId') || '');
      if (!Number.isNaN(anchorId) && anchorId > 0) {
        const index = this.filteredContacts.findIndex(c => c.id === anchorId);
        if (index >= 0) {
          this.highlightedContactId = anchorId;
          this.cdr.detectChanges();

          setTimeout(() => {
            this.scroller?.scrollToIndex(index);
            setTimeout(() => {
              this._mgmtHelper.scrollToElementById(`contact-${anchorId}`);
              setTimeout(() => {
                this.highlightedContactId = null;
                this.cdr.detectChanges();
              }, 2000);
            }, 300);
          }, 100);
        }
      }

      this.cdr.detectChanges();
    });

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      if (searchTerm.length >= 2) {
        this.isLoading = true;
        this._contactRepository.searchContacts(searchTerm).pipe(
          take(1),
          finalize(() => {
            this.isLoading = false;
            this.cdr.detectChanges();
          })
        ).subscribe((contacts: ContactModel[] | null) => {
          this.searchedContacts = (contacts ?? []).filter(contact => this.belongsToTab(contact));
          this.visibleContacts = [...this.searchedContacts];
          this.cdr.detectChanges();
          this.scrollToTop();
        });
      } else {
        this.searchedContacts = [];
        // Nach dem Löschen der Suche: Filtere wieder nach dem aktuell selektierten Buchstaben
        this.filterByLetter(this.selectedLetter || this.alphabet[0]);
        this.cdr.detectChanges();
      }
    });
  }

  filterByLetter(letter: string): void {
    this.selectedLetter = letter;
    this.filteredContacts = [...(this.groupedContacts[letter] || [])];
    this.visibleContacts = [...this.filteredContacts];
    this.cdr.detectChanges();
    this.scrollToTop();
  }

  scrollToTop(): void {
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.scroller?.scrollToIndex(0);
      });
    });
  }

  onSearchChange(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    this.searchText = inputValue;
    this.searchSubject.next(inputValue);
  }

  clearSearch(): void {
    this.searchText = '';
    this.searchSubject.next('');
    this.searchedContacts = [];
    this.filterByLetter(this.selectedLetter || this.alphabet[0]);
    this.cdr.detectChanges();
  }

  toggleCreateContact(show: boolean): void {
    this.openCreateContactModal = show;
  }

  onSetNewContact($event: any): void {
    this.newContact = $event;
  }

  openContactDetails(contact: ContactModel) {
    if (this.mergeMode) {
      this.selectForMerge(contact);
      return;
    }

    const letterFromContact = this.getGroupKey(contact.lastName ?? '');
    const letter =
      (letterFromContact && letterFromContact !== '?')
        ? letterFromContact
        : (this.selectedLetter || '#');

    this.router.navigate(
      ['/kontakte', contact.id],
      { queryParams: { letter, anchorId: contact.id } }
    );
  }

  toggleEditContact($event: Event, show: boolean, contact?: ContactModel): void {
    if ($event && typeof ($event as Event).stopPropagation === 'function') {
      $event.stopPropagation();
    }
    if (this.mergeMode && contact) {
      this.selectForMerge(contact);
      return;
    }

    if (contact) {
      this.newContact = {
        ...contact,
        birthday: contact.birthday ? new Date(contact.birthday) : null,
      };
    }
    this.openEditContactModal = show;
  }

  saveNewContact(): void {
    this._contactRepository.create(this.newContact).pipe(take(1)).subscribe((contact: ContactModel) => {
      if (!this.belongsToTab(contact)) {
        // z. B. im Tab „Kontakte“ als Tierarzt angelegt: gehört in den anderen Tab
        this.newContact = {} as ContactModel;
        this.openCreateContactModal = false;
        this.cdr.detectChanges();
        return;
      }

      const rawLastName = contact.lastName ?? '';
      const firstLetter = this.getGroupKey(rawLastName);

      if (!this.groupedContacts[firstLetter]) {
        this.groupedContacts[firstLetter] = [];
      }

      this.groupedContacts[firstLetter] = [
        ...this.groupedContacts[firstLetter],
        contact
      ].sort((a, b) => (a.lastName ?? '').localeCompare(b.lastName ?? ''));

      this.newContact = {} as ContactModel;
      this.openCreateContactModal = false;
      this.filterByLetter(firstLetter);

      setTimeout(() => {
        this.highlightedContactId = contact.id;
        this.cdr.detectChanges();

        const index = this.filteredContacts.findIndex(c => c.id === contact.id);
        if (index !== -1 && this.scroller) {
          this.scroller.scrollToIndex(index);
        }

        setTimeout(() => {
          this._mgmtHelper.scrollToElementById(`contact-${contact.id}`);
          setTimeout(() => {
            this.highlightedContactId = null;
            this.cdr.detectChanges();
          }, 2000);
        }, 300);
      }, 100);
    });
  }

  saveUpdateContact(): void {
    this._contactRepository.update(this.newContact, this.newContact.id).pipe(take(1)).subscribe((updatedContact: ContactModel) => {
      const newGroupKey = this.getGroupKey(updatedContact.lastName ?? '');

      let oldGroupKey: string | null = null;
      for (const key in this.groupedContacts) {
        if (this.groupedContacts[key].some(c => c.id === updatedContact.id)) {
          oldGroupKey = key;
          break;
        }
      }

      if (oldGroupKey && (oldGroupKey !== newGroupKey || !this.belongsToTab(updatedContact))) {
        this.groupedContacts[oldGroupKey] = this.groupedContacts[oldGroupKey].filter(c => c.id !== updatedContact.id);
      }

      if (!this.belongsToTab(updatedContact)) {
        // Tierarzt-Flag umgeschaltet: der Kontakt wechselt in den anderen Tab
        this.searchedContacts = this.searchedContacts.filter(c => c.id !== updatedContact.id);
        this.newContact = {} as ContactModel;
        this.openEditContactModal = false;
        this.filterByLetter(oldGroupKey ?? this.selectedLetter);
        return;
      }

      if (!this.groupedContacts[newGroupKey]) {
        this.groupedContacts[newGroupKey] = [];
      }

      this.groupedContacts[newGroupKey] = [
        ...this.groupedContacts[newGroupKey].filter(c => c.id !== updatedContact.id),
        updatedContact
      ].sort((a, b) => (a.lastName?.toLowerCase() || "").localeCompare(b.lastName?.toLowerCase() || ""));

      this.newContact = {} as ContactModel;
      this.openEditContactModal = false;
      this.filterByLetter(newGroupKey);
      this.cdr.detectChanges();
    });
  }

  toggleMergeMode(): void {
    this.mergeMode = !this.mergeMode;
    this.selectedMergeContacts = [];
  }

  selectForMerge(contact: ContactModel): void {
    if (!this.mergeMode) return;

    const exists = this.selectedMergeContacts.some(c => c.id === contact.id);
    if (exists) {
      this.selectedMergeContacts = this.selectedMergeContacts.filter(c => c.id !== contact.id);
    } else {
      this.selectedMergeContacts.push(contact);
    }
  }

  isSelected(contact: ContactModel): boolean {
    return this.selectedMergeContacts.some(c => c.id === contact.id);
  }

  startMerge(): void {
    if (this.selectedMergeContacts.length < 2) return;

    const [primary, ...others] = this.selectedMergeContacts;
    const merged: Partial<Record<keyof ContactModel, any>> = {...primary};
    const conflictMap: Partial<Record<keyof ContactModel, string[]>> = {};

    for (const key of Object.keys(primary) as (keyof ContactModel)[]) {
      const allValues = [primary, ...others]
        .map(c => c[key])
        .filter(v => v !== undefined && v !== null && v !== '');

      const distinctValues = Array.from(new Set(allValues.map(v => String(v))));

      if (distinctValues.length > 1) {
        conflictMap[key] = distinctValues;
        merged[key] = undefined;
      } else if ((primary[key] === undefined || primary[key] === null || primary[key] === '') && distinctValues.length === 1) {
        merged[key] = allValues[0];
      }
    }

    this.mergedContact = merged;
    this.conflictingFields = conflictMap;
    this.openMergeModal = true;
  }

  onMergeConfirmed(merged: Partial<ContactModel>) {
    const oldIds = this.selectedMergeContacts.map(c => c.id);
    this._waitingDialogService.showWaitingDialog(true);
    this._contactRepository.mergeContacts(merged, oldIds).subscribe({
      next: (newContact) => {
        const newId = newContact?.id;
        if (!newId) {
          console.error('Merge erfolgreich, aber keine ID im Response');
          return;
        }

        const rawLastName = newContact.lastName ?? '';
        const firstLetter = this.getGroupKey(rawLastName);

        if (!this.groupedContacts[firstLetter]) {
          this.groupedContacts[firstLetter] = [];
        }

        this.groupedContacts[firstLetter] = [
          ...this.groupedContacts[firstLetter],
          newContact
        ].sort((a, b) => (a.lastName ?? '').localeCompare(b.lastName ?? ''));

        for (const group in this.groupedContacts) {
          this.groupedContacts[group] = this.groupedContacts[group].filter(c => !oldIds.includes(c.id));
        }
        this._originRepository.getAll().subscribe();
        this._whereAboutRepository.getAll().subscribe();

        // Nach dem Mergen: Filtere nach dem Buchstaben des neuen Kontakts
        this.filterByLetter(firstLetter);
        this.openMergeModal = false;
        this.toggleMergeMode();
        this._waitingDialogService.showWaitingDialog(false);
        setTimeout(() => {
          this.highlightedContactId = newId;
          this.cdr.detectChanges();

          const index = this.filteredContacts.findIndex(c => c.id === newId);
          if (index !== -1 && this.scroller) {
            this.scroller.scrollToIndex(index);
          }

          setTimeout(() => {
            this._mgmtHelper.scrollToElementById(`contact-${newId}`);
            setTimeout(() => {
              this.highlightedContactId = null;
              this.cdr.detectChanges();
            }, 2000);
          }, 300);
        }, 100);
      },
      error: (err) => {
        console.error('Fehler beim Mergen:', err);
        this._waitingDialogService.showWaitingDialog(false);
      }
    });
  }

  onMergeCanceled() {
    this.openMergeModal = false;
  }

  trackByContact(index: number, contact: ContactModel): number {
    return contact.id;
  }

  private belongsToTab(contact: ContactModel | null | undefined): boolean {
    return this.onlyVeterinarians ? !!contact?.isVeterinary : !contact?.isVeterinary;
  }

  private getGroupKey(value: string): string {
    const first = (value || '').trim().charAt(0).toUpperCase() || '#';
    if (first === '#') return '#';
    return /^[A-Z]$/.test(first) ? first : '?';
  }
}
